import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { X, ExternalLink, Star, Loader2, Download, ChevronDown, ChevronRight, ArrowLeft } from 'lucide-react';
import { useData } from '../context/DataContext';
import { themeColor, actionColor, COMMENTER_COLOR } from '../constants/chips';

// 80,000-Hours-style slide-over: full-height panel anchored to the right,
// with an overlay scrim behind it. Closes on overlay click, Esc, or the
// X button. Comment text loads lazily for long submissions.

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  opacity: ${props => (props.$open ? 1 : 0)};
  visibility: ${props => (props.$open ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0s linear ${props => (props.$open ? 0 : 0.2)}s;
  /* Clicking the scrim closes the drawer. Body scroll is NOT locked (see below),
     so mousing over the table still scrolls the page behind the scrim. */
  cursor: pointer;
  /* Above the sticky Navbar (z-index 1000) so the drawer's own header/buttons
     are never covered. */
  z-index: 1100;
`;

const Drawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: min(640px, 100%);
  background-color: var(--bg-card);
  border-left: 1px solid var(--color-border);
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.06);
  transform: translateX(${props => (props.$open ? '0' : '100%')});
  transition: transform 0.25s ease;
  z-index: 1101;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--color-border);
  flex: 0 0 auto;
`;

const HeaderTitle = styled.div`
  font-family: var(--font-system);
  font-size: 0.8125rem;
  color: var(--color-text-muted);

  /* On small screens the Back button + close need the room. */
  @media (max-width: 600px) {
    display: none;
  }
`;

// Explicit, labeled way out of the panel — important on mobile, where the
// drawer is full-screen and the corner X alone is easy to miss.
const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-accent);
  font-family: var(--font-system);
  font-size: 0.875rem;
  font-weight: 500;
  padding: 6px 8px 6px 0;
  border-radius: var(--radius-sm);

  &:hover { color: var(--color-accent-hover); }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-muted);
  padding: 6px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;

  &:hover {
    color: var(--color-text);
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 1.5rem 1.5rem 4rem 1.5rem;

  @media (max-width: 600px) {
    padding: 1rem 1rem 4rem 1rem;
  }
`;

const Title = styled.h1`
  font-family: var(--font-lora);
  font-weight: 600;
  font-size: 1.5rem;
  line-height: 1.25;
  margin: 0 0 0.25rem 0;
  display: flex;
  align-items: baseline;
  gap: 8px;

  .featured-icon {
    color: #ca8a04;
    flex: 0 0 auto;
  }
`;

// Joint / multi-stakeholder filing: a labeled block listing the co-signers.
const JointBox = styled.div`
  margin: 0.25rem 0 1rem 0;

  .joint-label {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-accent);
    margin-bottom: 0.375rem;
  }

  .sigs {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
  }
`;

const SignatoryChip = styled.span`
  font-size: 0.75rem;
  background: rgba(28, 117, 188, 0.08);
  color: var(--color-text);
  border: 1px solid rgba(28, 117, 188, 0.18);
  padding: 2px 8px;
  border-radius: 99px;
`;

// Stacked meta blocks: an uppercase label header with its tags on the line below.
const MetaStack = styled.div`
  margin: 1rem 0 1.5rem 0;
`;

const Field = styled.div`
  margin-bottom: 0.875rem;
`;

const FieldLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin-bottom: 0.375rem;
`;

const MetaLine = styled.div`
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  margin: -0.125rem 0 0.875rem 0;
`;

const SectionHeader = styled.h3`
  font-family: var(--font-system);
  font-weight: 600;
  font-size: 0.6875rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin: 1.5rem 0 0.5rem 0;
`;

// Collapsible variant of SectionHeader — a button that toggles a section.
const SectionToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-system);
  font-weight: 600;
  font-size: 0.6875rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin: 1.5rem 0 0.5rem 0;

  &:hover { color: var(--color-accent); }
  .chevron { flex: 0 0 auto; }
`;

const Quote = styled.blockquote`
  border-left: 3px solid var(--color-accent);
  padding: 0.5rem 0 0.5rem 1rem;
  margin: 0.5rem 0 1rem 0;
  font-family: var(--font-system);
  font-style: normal;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--color-text);
`;

// Static colored tag (recommended action / commenter type / high-level topic).
const Tag = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 2px 9px;
  border-radius: 99px;
  background: ${p => p.$bg};
  color: ${p => p.$fg};
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const PdfFrame = styled.iframe`
  width: 100%;
  height: 62vh;
  min-height: 420px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--bg-subtle);
  margin-bottom: 0.5rem;
`;

const PdfBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0.25rem 0 0.5rem 0;
  font-size: 0.8125rem;
  color: var(--color-text-muted);

  a {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--color-accent);
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

const FullText = styled.pre`
  font-family: var(--font-system);
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--color-text);
  white-space: pre-wrap;
  word-break: break-word;
  background-color: rgba(0, 0, 0, 0.025);
  padding: 1rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  margin: 0.5rem 0 1rem 0;
  max-height: 400px;
  overflow-y: auto;
`;

const TextLoader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  padding: 1rem 0;
`;

const ExternalLinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: var(--color-accent);
  text-decoration: none;
  margin-top: 0.5rem;

  &:hover {
    color: var(--color-accent-hover);
    text-decoration: underline;
  }
`;

const EditorNoteBox = styled.div`
  margin: 0.5rem 0 1.5rem 0;
  padding: 0.875rem 1rem;
  background-color: rgba(202, 138, 4, 0.1);
  border-left: 4px solid #ca8a04;
  border-radius: 0 var(--radius-md) var(--radius-md) 0;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--color-text);

  .label {
    font-weight: 600;
    color: #92710c;
    font-size: 0.6875rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    display: block;
    margin-bottom: 0.25rem;
  }
`;

const InferredBox = styled.div`
  margin: 0.5rem 0 1.5rem 0;
  padding: 0.75rem 1rem;
  background-color: rgba(0, 0, 0, 0.03);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--color-text);

  .label {
    font-weight: 600;
    color: var(--color-text-muted);
    font-size: 0.6875rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    display: block;
    margin-bottom: 0.25rem;
  }

  .basis {
    margin-top: 0.5rem;
    font-style: italic;
    color: var(--color-text-muted);
    font-size: 0.8125rem;
  }
`;

// "N identical copies" badge shown near the top of the drawer for the
// representative of a 3+ duplicate group.
const DuplicateBadge = styled.div`
  display: inline-block;
  margin: 0.25rem 0 1rem 0;
  padding: 0.25rem 0.625rem;
  background-color: rgba(202, 138, 4, 0.1);
  border: 1px solid rgba(202, 138, 4, 0.35);
  border-radius: 99px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #92710c;
`;

// Bottom-of-drawer list linking each identical copy to its regulations.gov page.
const CopiesBox = styled.div`
  margin: 1rem 0 0 0;
  font-size: 0.8125rem;
  line-height: 1.6;

  .label {
    font-weight: 600;
    color: var(--color-text-muted);
    font-size: 0.6875rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    display: block;
    margin-bottom: 0.375rem;
  }
  a { color: var(--color-accent); display: inline-block; margin-right: 0.75rem; }
`;

// Clickable topic chip in the drawer — colored by theme, toggles that topic in
// the table's filter. Shows a ring when it's an active filter.
const TopicFilterChip = styled.button`
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 2px 9px;
  border-radius: 99px;
  cursor: pointer;
  background: ${p => p.$bg};
  color: ${p => p.$fg};
  border: 0;
  box-shadow: ${p => (p.$selected ? '0 0 0 2px var(--color-text)' : 'none')};
  transition: filter 0.12s ease, box-shadow 0.12s ease;
  &:hover { filter: brightness(0.92); }
`;

const ChipHint = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin: 0 0 0.4rem 0;
`;

function fmtDate(s) {
  if (!s) return '—';
  return s.slice(0, 10);
}

function DetailDrawer({ submission, parentNameById, onClose, onSelectTopic, activeTopicIds }) {
  const parentName = parentNameById || {};
  const activeTopics = activeTopicIds || [];
  const open = Boolean(submission);
  const { getCommentText } = useData();
  const [commentText, setCommentText] = useState(null);
  const [textLoading, setTextLoading] = useState(false);
  // Additional attachments (2nd, 3rd PDF) render collapsed by default so we don't
  // load several heavy iframes at once; keyed by index, reset per submission.
  const [expandedPdfs, setExpandedPdfs] = useState({});
  useEffect(() => { setExpandedPdfs({}); }, [submission?.doc_id]);

  // Lazy-load comment text on open. Inline texts are cached by DataContext
  // so this returns immediately for short submissions.
  useEffect(() => {
    let cancelled = false;
    if (!submission) {
      setCommentText(null);
      return () => {};
    }
    // For comments with a PDF we show only the raw typed comment (inline_comment),
    // never the attachment's extracted text — so skip the lazy assembled-text fetch.
    if (submission.attachment_pdf) {
      setCommentText(null);
      setTextLoading(false);
      return () => {};
    }
    if (submission.comment_text_inline) {
      setCommentText(submission.comment_text_inline);
      setTextLoading(false);
      return () => {};
    }
    setCommentText(null);
    setTextLoading(true);
    getCommentText(submission.doc_id).then(text => {
      if (cancelled) return;
      setCommentText(text);
      setTextLoading(false);
    });
    return () => { cancelled = true; };
  }, [submission, getCommentText]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Intentionally do NOT lock body scroll: the drawer is a non-modal side panel,
  // so the table behind stays scrollable when the pointer is over it (the drawer
  // has its own overflow-y for its content).

  return (
    <>
      <Overlay $open={open} onClick={onClose} aria-hidden={!open} />
      <Drawer
        $open={open}
        role="dialog"
        /* Non-modal by design: body scroll is not locked and the table stays
           interactive behind it, so do NOT advertise aria-modal (that would tell
           assistive tech the background is inert when it isn't). */
        aria-modal="false"
        aria-labelledby="drawer-title"
      >
        {submission && (
          <>
            <Header>
              <BackButton onClick={onClose} aria-label="Back to results">
                <ArrowLeft size={16} /> Back
              </BackButton>
              <HeaderTitle>
                Submission · {submission.doc_id}
              </HeaderTitle>
              <CloseButton onClick={onClose} aria-label="Close detail panel">
                <X size={18} />
              </CloseButton>
            </Header>
            <Body>
              <Title id="drawer-title">
                {submission.featured && (
                  <Star className="featured-icon" size={18} fill="#ca8a04" />
                )}
                {submission.name}
              </Title>
              {submission.duplicate_count > 1 && (
                <DuplicateBadge>
                  {submission.duplicate_count} identical copies submitted
                </DuplicateBadge>
              )}
              {submission.is_joint && submission.signatories?.length > 0 && (
                <JointBox>
                  <div className="joint-label">
                    Joint submission · {submission.signatory_count} organizations
                  </div>
                  <div className="sigs">
                    {submission.signatories.map(sig => (
                      <SignatoryChip key={sig}>{sig}</SignatoryChip>
                    ))}
                  </div>
                </JointBox>
              )}
              {submission.inferred_submitter && (
                <InferredBox>
                  <span className="label">
                    Inferred submitter · {submission.inferred_submitter.confidence} confidence
                  </span>
                  {submission.inferred_submitter.guess}
                  {submission.inferred_submitter.quote && (
                    <div className="basis">Basis: "{submission.inferred_submitter.quote}"</div>
                  )}
                </InferredBox>
              )}

              {submission.editor_note && (
                <EditorNoteBox>
                  <span className="label">Editor's note</span>
                  {submission.editor_note}
                </EditorNoteBox>
              )}

              <MetaStack>
                <MetaLine>Posted {fmtDate(submission.posted_date)}</MetaLine>
                {submission.commenter_type && (
                  <Field>
                    <FieldLabel>Commenter type</FieldLabel>
                    <TagRow>
                      <Tag $bg={COMMENTER_COLOR.bg} $fg={COMMENTER_COLOR.fg}>
                        {submission.commenter_type}
                        {submission.employer_subtype ? ` · ${submission.employer_subtype}` : ''}
                      </Tag>
                      {(submission.commenter_type_secondary || []).map(t => (
                        <Tag key={t} $bg={COMMENTER_COLOR.bg} $fg={COMMENTER_COLOR.fg}>{t}</Tag>
                      ))}
                    </TagRow>
                  </Field>
                )}
                {submission.parent_ids && submission.parent_ids.length > 0 && (
                  <Field>
                    <FieldLabel>Topics</FieldLabel>
                    <ChipHint>Click a topic to filter the table by it.</ChipHint>
                    <TagRow>
                      {submission.parent_ids.map(pid => {
                        const c = themeColor(pid);
                        const selected = activeTopics.includes(pid);
                        return (
                          <TopicFilterChip
                            key={pid}
                            $bg={c.bg}
                            $fg={c.fg}
                            $selected={selected}
                            aria-pressed={selected}
                            onClick={() => onSelectTopic && onSelectTopic(pid)}
                          >
                            {parentName[pid] || pid}
                          </TopicFilterChip>
                        );
                      })}
                    </TagRow>
                  </Field>
                )}
                {submission.recommended_actions && submission.recommended_actions.length > 0 && (
                  <Field>
                    <FieldLabel>Recommended action</FieldLabel>
                    <TagRow>
                      {submission.recommended_actions.map(a => {
                        const c = actionColor(a);
                        return <Tag key={a} $bg={c.bg} $fg={c.fg}>{a}</Tag>;
                      })}
                    </TagRow>
                  </Field>
                )}
              </MetaStack>

              {submission.representative_quote && (
                <>
                  <SectionHeader>Representative quote</SectionHeader>
                  <Quote>"{submission.representative_quote}"</Quote>
                </>
              )}

              {(() => {
                // Prefer the multi-PDF list; fall back to the legacy scalar field.
                const pdfs = submission.attachment_pdfs?.length
                  ? submission.attachment_pdfs
                  : (submission.attachment_pdf
                      ? [{ url: submission.attachment_pdf, name: 'Attachment' }]
                      : []);
                if (pdfs.length === 0) return null;
                const totalPdfs = submission.attachment_pdf_total || pdfs.length;
                const extraNotBundled = Math.max(0, totalPdfs - pdfs.length);
                const [first, ...rest] = pdfs;
                return (
                  <>
                    <SectionHeader>
                      {pdfs.length > 1 ? `Attached documents (${totalPdfs})` : 'Attached document'}
                    </SectionHeader>
                    <PdfFrame
                      src={first.url}
                      title={`Attached document for ${submission.doc_id}`}
                    />
                    <PdfBar>
                      <a href={first.url} target="_blank" rel="noopener noreferrer">
                        <Download size={13} /> Open / download PDF
                      </a>
                      <span>
                        {pdfs.length > 1
                          ? `Attachment 1 of ${totalPdfs}`
                          : "If it doesn't display, use the link above. A text version is below."}
                      </span>
                    </PdfBar>

                    {rest.map((pdf, i) => {
                      const idx = i + 1; // position within pdfs (0-based)
                      const open = Boolean(expandedPdfs[idx]);
                      return (
                        <div key={pdf.url}>
                          <SectionToggle
                            type="button"
                            aria-expanded={open}
                            onClick={() => setExpandedPdfs(m => ({ ...m, [idx]: !m[idx] }))}
                          >
                            {open
                              ? <ChevronDown className="chevron" size={13} />
                              : <ChevronRight className="chevron" size={13} />}
                            Attachment {idx + 1} of {totalPdfs}
                          </SectionToggle>
                          {open && (
                            <>
                              <PdfFrame
                                src={pdf.url}
                                title={`Attachment ${idx + 1} for ${submission.doc_id}`}
                              />
                              <PdfBar>
                                <a href={pdf.url} target="_blank" rel="noopener noreferrer">
                                  <Download size={13} /> Open / download PDF
                                </a>
                              </PdfBar>
                            </>
                          )}
                        </div>
                      );
                    })}

                    {extraNotBundled > 0 && (
                      <PdfBar>
                        <span>
                          {extraNotBundled} more attachment{extraNotBundled > 1 ? 's' : ''} not shown here —{' '}
                          <a href={submission.regulations_gov_url} target="_blank" rel="noopener noreferrer">
                            view all on regulations.gov
                          </a>.
                        </span>
                      </PdfBar>
                    )}
                  </>
                );
              })()}

              <SectionHeader>Comment text</SectionHeader>
              {submission.attachment_pdf ? (
                // PDF comments: show only the submitter's typed comment, not the
                // attachment's extracted text (the PDF itself is rendered above).
                submission.inline_comment ? (
                  <FullText>{submission.inline_comment}</FullText>
                ) : (
                  <TextLoader>No typed comment — see the attached document above.</TextLoader>
                )
              ) : (
                <>
                  {textLoading && (
                    <TextLoader>
                      <Loader2 size={14} className="spin" />
                      Loading…
                    </TextLoader>
                  )}
                  {!textLoading && commentText && <FullText>{commentText}</FullText>}
                  {!textLoading && !commentText && (
                    <TextLoader>No comment text available for this submission.</TextLoader>
                  )}
                </>
              )}

              {submission.main_recommendation && (
                <>
                  <SectionHeader>Summary</SectionHeader>
                  <p style={{ margin: '0.25rem 0 1rem 0', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {submission.main_recommendation}
                  </p>
                </>
              )}

              <ExternalLinkButton
                href={submission.regulations_gov_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                View on regulations.gov <ExternalLink size={14} />
              </ExternalLinkButton>

              {submission.duplicate_copies?.length > 0 && (
                <CopiesBox>
                  <span className="label">
                    Identical copies ({submission.duplicate_copies.length})
                  </span>
                  {submission.duplicate_copies.map(c => (
                    <a key={c.doc_id} href={c.url} target="_blank" rel="noopener noreferrer">
                      {c.doc_id}
                    </a>
                  ))}
                </CopiesBox>
              )}
            </Body>
          </>
        )}
      </Drawer>
    </>
  );
}

export default DetailDrawer;
